/* ══════════════════════════════════════════════════════════════════════════
   Calculadora de finiquito — página de aterrizaje
   ──────────────────────────────────────────────────────────────────────────
   "cuánto me corresponde por despido" y "como calcular finiquito por despido"
   se llevaron 6.734 pesos en un mes y aterrizaban en la página del área o en
   la portada. Quien busca eso no quiere una consulta: quiere su número. Se lo
   damos primero, y el contacto viene después, cuando ya vio la cifra y quiere
   saber si es la correcta.

   La calculadora ya existía como sección de la portada. Enlazarla con /#finiquito
   funcionaba, pero obligaba a cargar la portada entera y a desplazarse por
   delante de todo: mal aterrizaje en el teléfono, de donde llega el 96% del
   tráfico de los anuncios. Aquí abre directa.

   URL FINAL PARA GOOGLE ADS: /calculadora-finiquito
   ══════════════════════════════════════════════════════════════════════════ */

import React from 'react';
import { LogoSVG, WhatsAppIcon } from '../../components/Icons';
import { Finiquito, Footer, WhatsAppButton } from '../../components';
import { GoogleRatingBadge } from '../../components/Resenas/Resenas';
import { waLink, promesaDeRespuesta } from '../../utils/whatsapp';
import { usePageMeta } from '../../utils/usePageMeta';

/* Las clases dl-* son el armazón compartido de las páginas interiores
   (cabecera y portada); viven en la hoja de Derecho Laboral por ser la
   primera que las necesitó. */
import '../DerechoLaboral/DerechoLaboral.css';
import './CalculadoraFiniquito.css';

const WA_MSG = 'Hola, calculé mi finiquito en su página web y quisiera revisarlo con una abogada. ¿Pueden ayudarme?';

const CalculadoraFiniquito: React.FC = () => {
  const waUrl = waLink(WA_MSG);

  usePageMeta(
    'Calculadora de Finiquito 2026 | Cuánto me corresponde por despido | Souza & Villa',
    'Calcula gratis y sin registro cuánto te corresponde de finiquito según la causal de término. Abogadas laboralistas en Concepción revisan tu cálculo.',
    '/calculadora-finiquito',
  );

  return (
    <div className="dl-page">
      <header className="dl-header">
        <a href="/" className="dl-logo">
          <LogoSVG size={44} />
          <div className="dl-logo-text">
            <span className="dl-logo-name">Souza &amp; Villa</span>
            <span className="dl-logo-tagline">Abogadas</span>
          </div>
        </a>
        <div className="dl-header-actions">
          <a href="/#areas" className="dl-back-link">← Volver al inicio</a>
          <a href={waUrl} target="_blank" rel="noopener noreferrer" className="dl-btn-wa">
            Consulta gratis
          </a>
        </div>
      </header>

      <section className="dl-hero">
        <div className="dl-hero-inner">
          <div className="dl-hero-label">Despido y finiquito</div>
          <h1 className="dl-hero-title">¿Cuánto te corresponde por <em>despido</em>?</h1>
          <p className="dl-hero-desc">
            Calcula el monto de tu finiquito según la causal con que te
            despidieron. Es gratis, no pide registro y el resultado es tuyo en
            un minuto. <strong className="cf-plazo">Si el despido fue injustificado, tienes 60 días hábiles para demandar.</strong>
          </p>
          <a href="#finiquito" className="cf-btn-calcular">
            Calcular mi finiquito ↓
          </a>
          <div className="dl-hero-respuesta">{promesaDeRespuesta()} · Primera consulta gratis</div>
          <GoogleRatingBadge className="rating-badge--hero dl-hero-rating" />
        </div>
      </section>

      <Finiquito />

      <section className="cf-cierre">
        <p className="cf-cierre-texto">
          El cálculo es una estimación con las reglas del Código del Trabajo. Lo
          que finalmente se paga depende de la causal, de las cotizaciones al
          día y de lo que diga el contrato: por eso conviene que una abogada
          mire tu caso antes de firmar nada.
        </p>
        <a href={waUrl} target="_blank" rel="noopener noreferrer" className="btn-whatsapp cf-cierre-btn">
          <WhatsAppIcon size={17} /> Revisar mi finiquito con una abogada
        </a>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default CalculadoraFiniquito;
